import React, { useState, useRef, useCallback } from "react";
import { Avatar } from "../../../../../ui/components/Avatar";
import { Badge } from "../../../../../ui/components/Badge";
import { QueryStatusBadge } from "../../../../../ui/components/QueryStatusBadge";
import { Button } from "../../../../../ui/components/Button";
import { IconButton } from "../../../../../ui/components/IconButton";
import { IconWithBackground } from "../../../../../ui/components/IconWithBackground";
import { TextField } from "../../../../../ui/components/TextField";
import { DialogLayout } from "../../../../../ui/layouts/DialogLayout";
import {
  FeatherX,
  FeatherUpload,
  FeatherFileText,
  FeatherTrash2,
  FeatherDownload,
  FeatherClock,
  FeatherCheck,
  FeatherHelpCircle,
  FeatherGitBranch,
} from "@subframe/core";
import { formatDate, getRelativeTime } from "../../../../../utils";
import { queriesApi } from "../../../../../shared/queries/data/api/queriesApi";
import { QueryAttachmentDTO } from "../../../../../shared/queries/data/models/QueryDTO";
import { useToast } from "../../../../../shared/presentation/components";

// Clean Architecture: Use hooks and entities
import { useQueryDetails } from "../../../../../shared/queries/presentation/hooks/useQueryDetails";
import { useAddComment } from "../../../../../shared/queries/presentation/hooks/useAddComment";
import { QueryEntity } from "../../../../../shared/queries/domain/entities/QueryEntity";
import QueryRaiseModal from "../../../../../shared/queries/presentation/components/QueryRaiseModal";
import QueryDetailModal from "../../../../../shared/queries/presentation/components/QueryDetailModal";

interface QueryUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  queryId: number;
  queryTitle: string;
  onUploadComplete?: () => void;
}

interface PendingUpload {
  id: string;
  file: File;
  status: "pending" | "uploading" | "completed" | "error";
  error?: string;
  attachmentId?: number;
  signed_url?: string;
}

const formatTime = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const QueryUploadModal: React.FC<QueryUploadModalProps> = ({
  open,
  onOpenChange,
  queryId,
  queryTitle,
  onUploadComplete,
}) => {
  const { showSuccess, showError } = useToast();
  const { query, loading, refetch } = useQueryDetails(queryId);
  const { addComment, adding: submittingComment } = useAddComment();

  const [comment, setComment] = React.useState("");
  const [isSubQueryModalOpen, setIsSubQueryModalOpen] = React.useState(false);
  const [isRaiseSubQueryModalOpen, setIsRaiseSubQueryModalOpen] =
    React.useState(false);
  const [selectedSubQueryId, setSelectedSubQueryId] = React.useState<
    number | undefined
  >();

  // Local query state for optimistic updates
  const [localQuery, setLocalQuery] = React.useState<QueryEntity | null>(null);

  // Upload queue system
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const uploadQueueRef = useRef<boolean>(false);
  const queuedFilesRef = useRef<Set<string>>(new Set());
  const processingFileRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragCounter = useRef<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // Always refetch when modal opens (when open changes from false to true)
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (open && !prevOpenRef.current && queryId) {
      // Modal just opened - always refetch to get latest data including attachments
      refetch();
    }
    prevOpenRef.current = open;
  }, [open, queryId, refetch]);

  // Clear local query when modal closes
  React.useEffect(() => {
    if (!open) {
      setLocalQuery(null);
    }
  }, [open]);

  // Update local query when the fetched query changes
  React.useEffect(() => {
    if (query) {
      setLocalQuery(query);
    }
  }, [query]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleAddComment = async () => {
    if (!comment.trim() || !queryId || submittingComment) return;

    const result = await addComment(queryId, comment.trim());

    if (result.isSuccess) {
      setComment("");

      // Optimistically update local query with the new comment
      if (localQuery && result.value) {
        const newComment = result.value;
        setLocalQuery({
          ...localQuery,
          comments: [...(localQuery.comments || []), newComment],
        });
      }

      // Refetch to get latest data
      refetch();
    } else {
      showError(
        result.error || "Failed to add comment. Please try again."
      );
    }
  };

  const handleSubQueryClick = (subQueryId: number) => {
    setSelectedSubQueryId(subQueryId);
    setIsSubQueryModalOpen(true);
  };

  const handleSubQueryUpdate = (
    subQueryId: number,
    updates: Partial<QueryEntity>
  ) => {
    // Refetch to get updated data
    refetch();
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  // Process upload queue
  const processUploadQueue = useCallback(async () => {
    if (!queryId || uploadQueueRef.current) {
      return;
    }

    uploadQueueRef.current = true;

    while (true) {
      let pending: PendingUpload | null = null;
      let queueLength = 0;

      await new Promise<void>((resolve) => {
        setPendingUploads((prev) => {
          queueLength = prev.filter((u) => u.status === "pending").length;
          pending = prev.find((u) => u.status === "pending") || null;

          if (!pending) {
            resolve();
            return prev;
          }

          const updated = prev.map((u) =>
            u.id === pending!.id ? { ...u, status: "uploading" as const } : u
          );
          resolve();
          return updated;
        });
      });

      if (!pending || queueLength === 0) {
        uploadQueueRef.current = false;
        break;
      }

      const currentPending: PendingUpload = pending;

      try {
        const result = await queriesApi.uploadAttachment(
          queryId,
          currentPending.file
        );

        const fileKey = `${currentPending.file.name}_${currentPending.file.size}`;
        queuedFilesRef.current.delete(fileKey);

        setPendingUploads((current) =>
          current.map((u) =>
            u.id === currentPending.id
              ? {
                  ...u,
                  status: "completed" as const,
                  attachmentId:
                    typeof result.data.id === "string"
                      ? parseInt(result.data.id, 10)
                      : result.data.id,
                  signed_url: result.data.signed_url,
                }
              : u
          )
        );

        setTimeout(() => {
          setPendingUploads((current) =>
            current.filter((u) => u.id !== currentPending.id)
          );
        }, 1000);

        // Refetch to get updated attachments
        refetch();

        if (onUploadComplete) {
          onUploadComplete();
        }
      } catch (err) {
        const fileKey = `${currentPending.file.name}_${currentPending.file.size}`;
        queuedFilesRef.current.delete(fileKey);

        setPendingUploads((current) =>
          current.map((u) =>
            u.id === currentPending.id
              ? {
                  ...u,
                  status: "error" as const,
                  error: err instanceof Error ? err.message : "Upload failed",
                }
              : u
          )
        );

        showError(err instanceof Error ? err.message : "Failed to upload file");
      }
    }
  }, [queryId, showError, onUploadComplete, refetch]);

  const queueUpload = useCallback(
    (file: File) => {
      const fileKey = `${file.name}_${file.size}`;

      if (queuedFilesRef.current.has(fileKey)) {
        return;
      }

      queuedFilesRef.current.add(fileKey);

      const tempId = `temp_${Date.now()}_${Math.random()}`;
      const newUpload: PendingUpload = {
        id: tempId,
        file: file,
        status: "pending",
      };

      setPendingUploads((prev) => {
        const alreadyExists = prev.some(
          (u) =>
            u.file.name === file.name &&
            u.file.size === file.size &&
            (u.status === "pending" || u.status === "uploading")
        );

        if (alreadyExists) {
          queuedFilesRef.current.delete(fileKey);
          return prev;
        }

        const wasEmpty = prev.length === 0;
        const newQueue = [...prev, newUpload];

        if (wasEmpty && !uploadQueueRef.current) {
          setTimeout(() => {
            processUploadQueue();
          }, 0);
        }

        return newQueue;
      });
    },
    [processUploadQueue]
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileKey = `${file.name}_${file.size}_${file.lastModified}`;
    if (processingFileRef.current === fileKey) {
      return;
    }
    processingFileRef.current = fileKey;

    queueUpload(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setTimeout(() => {
      processingFileRef.current = null;
    }, 1000);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      queueUpload(file);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      await queriesApi.deleteAttachment(queryId, attachmentId);
      showSuccess("Attachment deleted successfully");
      refetch();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to delete attachment"
      );
    }
  };

  if (!query && !loading) {
    return (
      <DialogLayout open={open} onOpenChange={onOpenChange}>
        <div className="flex w-full justify-center py-8">
          <span className="text-body text-neutral-500">Query not found</span>
        </div>
      </DialogLayout>
    );
  }

  // Only show loading state if we have no local query data yet
  if (!localQuery && loading) {
    return (
      <DialogLayout open={open} onOpenChange={onOpenChange}>
        <div className="flex w-full justify-center py-8">
          <span className="text-body text-neutral-500">Loading...</span>
        </div>
      </DialogLayout>
    );
  }

  // If we don't have localQuery and not loading, query not found
  if (!localQuery) {
    return (
      <DialogLayout open={open} onOpenChange={onOpenChange}>
        <div className="flex w-full justify-center py-8">
          <span className="text-body text-neutral-500">Query not found</span>
        </div>
      </DialogLayout>
    );
  }

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-[640px] max-w-full flex-col items-start gap-6 bg-white px-8 py-8">
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font">
              QueryId-{localQuery.queryId}
            </span>
            <QueryStatusBadge
              status={localQuery.status}
              icon={
                localQuery.status === "open" ? (
                  <FeatherClock />
                ) : localQuery.status === "in_progress" ? (
                  <FeatherClock />
                ) : localQuery.status === "resolved" ? (
                  <FeatherCheck />
                ) : (
                  <FeatherX />
                )
              }
            >
              {localQuery.status.charAt(0).toUpperCase() +
                localQuery.status.slice(1).replace("_", " ")}
            </QueryStatusBadge>
          </div>

          <div className="flex items-center gap-2">
            {localQuery.status !== "resolved" && (
              <Button
                variant="neutral-tertiary"
                icon={<FeatherHelpCircle />}
                onClick={() => setIsRaiseSubQueryModalOpen(true)}
              >
                Create Sub-Query
              </Button>
            )}
            <IconButton icon={<FeatherX />} onClick={handleClose} />
          </div>
        </div>

        {/* Query Details Section */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Query Details
          </span>
          <div className="flex w-full flex-col items-start gap-4">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Title
              </span>
              <span className="text-body font-body text-neutral-500">
                {localQuery.title}
              </span>
            </div>
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Description
              </span>
              <span className="text-body font-body text-neutral-500">
                {localQuery.description || "No description provided"}
              </span>
            </div>

            <div className="flex w-full items-center gap-6 flex-wrap">
              <div className="flex flex-col items-start gap-1">
                <span className="text-caption font-caption text-subtext-color">
                  Priority
                </span>
                <Badge
                  variant={
                    localQuery.priority === "high"
                      ? "error"
                      : localQuery.priority === "medium"
                      ? "warning"
                      : "neutral"
                  }
                >
                  {localQuery.priority.toUpperCase()}
                </Badge>
              </div>
              <div className="flex flex-col items-start gap-1">
                <span className="text-caption font-caption text-subtext-color">
                  Raised By
                </span>
                <Badge className="bg-success-500 py-1 px-2">
                  <span className="text-purple">
                    {localQuery.raisedBy.userName.toUpperCase()}
                  </span>
                </Badge>
              </div>

              <div className="flex flex-col items-start gap-1">
                <span className="text-caption font-caption text-subtext-color">
                  Assigned To
                </span>
                <Badge className="bg-error-500 py-1 px-2">
                  <span className="text-white">
                    {localQuery.assignedTo?.userName.toUpperCase() ||
                      "UNASSIGNED"}
                  </span>
                </Badge>
              </div>
              <div className="flex flex-col items-start gap-1">
                <span className="text-caption font-caption text-subtext-color">
                  Created
                </span>
                <span className="text-body font-body text-neutral-500">
                  {formatDate(localQuery.createdAt.toISOString())} •{" "}
                  {formatTime(localQuery.createdAt.toISOString())}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Query Timeline Section - Only show if there are subqueries */}
        {localQuery.subQueries && localQuery.subQueries.length > 0 && (
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Query Timeline
            </span>
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex w-full flex-col items-start gap-4">
                <div className="flex w-full items-start gap-4">
                  <div className="flex flex-col items-center gap-2">
                    <IconWithBackground
                      variant="warning"
                      icon={<FeatherHelpCircle />}
                    />
                    <div className="flex w-px grow shrink-0 basis-0 flex-col items-center bg-neutral-200" />
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-white px-4 py-4">
                    <div className="flex w-full items-start justify-between">
                      <div className="flex flex-col items-start gap-2">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Main Query: {localQuery.title}
                        </span>
                      </div>
                      <QueryStatusBadge status={localQuery.status}>
                        {localQuery.status.charAt(0).toUpperCase() +
                          localQuery.status.slice(1).replace("_", " ")}
                      </QueryStatusBadge>
                    </div>
                    <span className="text-body font-body text-neutral-500">
                      {localQuery.description || "No description provided"}
                    </span>
                  </div>
                </div>
                {localQuery.subQueries.map((subquery, index) => (
                  <div
                    key={subquery.queryId}
                    className="flex w-full items-start gap-4 pl-8"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <IconWithBackground
                        icon={<FeatherGitBranch />}
                        variant={
                          subquery.status === "resolved" ? "success" : "neutral"
                        }
                      />
                      {index < localQuery.subQueries!.length - 1 && (
                        <div className="flex w-px grow shrink-0 basis-0 flex-col items-center bg-neutral-200" />
                      )}
                    </div>
                    <div
                      className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-white px-4 py-4 cursor-pointer hover:bg-neutral-50 transition-colors"
                      onClick={() => handleSubQueryClick(subquery.queryId)}
                    >
                      <div className="flex w-full items-start justify-between">
                        <div className="flex flex-col items-start gap-2">
                          <span className="text-body-bold font-body-bold text-default-font">
                            Sub-Query: {subquery.title}
                          </span>
                          <div className="flex items-center gap-2">
                            <Avatar size="x-small">
                              {subquery.raisedBy.userName
                                ?.substring(0, 2)
                                .toUpperCase() || "U"}
                            </Avatar>
                            <span className="text-caption font-caption text-subtext-color">
                              Created by {subquery.raisedBy.userName} •{" "}
                              {formatDate(subquery.createdAt.toISOString())}
                            </span>
                          </div>
                        </div>
                        <QueryStatusBadge status={subquery.status}>
                          {subquery.status === "resolved" ? "Resolved" : "New"}
                        </QueryStatusBadge>
                      </div>
                      <span className="text-body font-body text-neutral-500">
                        {subquery.description || "No description provided"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Attachments Section - Always show */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Attachments
            {localQuery.attachments && localQuery.attachments.length > 0
              ? ` (${localQuery.attachments.length})`
              : ""}
          </span>

          {/* Upload area - non-mandatory */}
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-body-bold font-body-bold text-default-font">
              Upload Documents
            </span>
            <div
              className={`flex h-32 w-full flex-none flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed ${
                isDragging
                  ? "border-brand-500 bg-brand-50"
                  : "border-neutral-300 bg-neutral-50"
              } px-4 py-4 cursor-pointer`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleFileSelect}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <FeatherUpload className="text-heading-2 font-heading-2 text-neutral-400" />
              <span className="text-body font-body text-neutral-500">
                Drag and drop files here
              </span>
              <span className="text-caption font-caption text-neutral-400">
                or click to browse
              </span>
            </div>
          </div>

          {/* Show pending uploads */}
          {pendingUploads.length > 0 && (
            <div className="flex w-full flex-col items-start gap-2">
              {pendingUploads.map((pending) => (
                <div
                  key={pending.id}
                  className="flex w-full items-center gap-3 rounded-md border border-solid border-neutral-border bg-white px-3 py-2"
                >
                  <FeatherFileText className="text-body font-body text-neutral-500" />
                  <div className="flex grow shrink-0 basis-0 flex-col items-start">
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      {pending.file.name}
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      {pending.status === "pending" && "Queued..."}
                      {pending.status === "uploading" && "Uploading..."}
                      {pending.status === "error" && pending.error}
                      {pending.status === "completed" && "Uploaded"}
                    </span>
                  </div>
                  {pending.status === "uploading" && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  )}
                  {pending.status === "error" && (
                    <IconButton
                      variant="neutral-tertiary"
                      size="small"
                      icon={<FeatherTrash2 />}
                      onClick={() => {
                        setPendingUploads((prev) =>
                          prev.filter((u) => u.id !== pending.id)
                        );
                      }}
                    />
                  )}
                  {pending.status === "completed" && pending.signed_url && (
                    <IconButton
                      variant="neutral-tertiary"
                      size="small"
                      icon={<FeatherDownload />}
                      onClick={() => {
                        window.open(pending.signed_url, "_blank");
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Show existing attachments */}
          {localQuery.attachments && localQuery.attachments.length > 0 ? (
            <div className="flex w-full flex-col items-start gap-2">
              {localQuery.attachments.map((attachment) => {
                const isPending = pendingUploads.some(
                  (p) =>
                    p.attachmentId ===
                    (typeof attachment.id === "string"
                      ? parseInt(attachment.id, 10)
                      : attachment.id)
                );
                if (isPending) return null;

                return (
                  <div
                    key={attachment.id}
                    className="flex w-full items-center gap-3 rounded-md border border-solid border-neutral-border bg-white px-3 py-2"
                  >
                    <FeatherFileText className="text-body font-body text-neutral-500" />
                    <div className="flex grow shrink-0 basis-0 flex-col items-start">
                      <span className="text-caption-bold font-caption-bold text-default-font">
                        {attachment.file_name || `Document ${attachment.id}`}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        {attachment.uploaded_at
                          ? getRelativeTime(attachment.uploaded_at)
                          : "Uploaded"}
                      </span>
                    </div>
                    {attachment.signed_url ? (
                      <Button
                        variant="neutral-tertiary"
                        size="small"
                        icon={<FeatherDownload />}
                        onClick={() => {
                          window.open(attachment.signed_url, "_blank");
                        }}
                      >
                        Download
                      </Button>
                    ) : (
                      <span className="text-caption font-caption text-subtext-color">
                        Loading...
                      </span>
                    )}
                    <IconButton
                      variant="neutral-tertiary"
                      size="small"
                      icon={<FeatherTrash2 />}
                      onClick={() =>
                        handleDeleteAttachment(
                          typeof attachment.id === "string"
                            ? parseInt(attachment.id, 10)
                            : attachment.id
                        )
                      }
                    />
                  </div>
                );
              })}
            </div>
          ) : pendingUploads.length === 0 ? (
            <div className="flex w-full justify-center py-4">
              <span className="text-body text-neutral-500">
                No attachments uploaded yet
              </span>
            </div>
          ) : null}
        </div>

        {/* Discussion Section */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Discussion
          </span>
          {!localQuery.comments || localQuery.comments.length === 0 ? (
            <div className="flex w-full justify-center py-4">
              <span className="text-body text-neutral-500">
                No comments yet
              </span>
            </div>
          ) : (
            <div className="flex w-full flex-col items-start gap-3">
              {localQuery.comments
                .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
                .map((comment) => (
                  <div
                    key={comment.commentId}
                    className="flex w-full items-start gap-2"
                  >
                    <Avatar size="small">
                      {comment.user.userName
                        ?.split("@")[0]
                        ?.substring(0, 2)
                        ?.toUpperCase() || "U"}
                    </Avatar>
                    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-body-bold font-body-bold text-default-font">
                          {comment.user.userName || "Unknown User"}
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          {comment.createdAt &&
                          !isNaN(comment.createdAt.getTime())
                            ? getRelativeTime(comment.createdAt.toISOString())
                            : "Just now"}
                        </span>
                      </div>
                      <span className="text-body font-body text-default-font">
                        {comment.comment || "No comment text"}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}
          <div className="flex w-full items-start gap-2">
            <Avatar size="small">CU</Avatar>
            <TextField
              className="h-auto grow shrink-0 basis-0"
              variant="filled"
              label=""
              helpText={
                submittingComment ? "Posting comment..." : "Press Enter to post"
              }
            >
              <TextField.Input
                placeholder="Add a comment..."
                value={comment}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setComment(e.target.value)
                }
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter" && !submittingComment) {
                    handleAddComment();
                  }
                }}
                disabled={submittingComment}
              />
            </TextField>
          </div>
        </div>
      </div>
      <QueryRaiseModal
        open={isRaiseSubQueryModalOpen}
        isCreateSubQuery={true}
        parentQueryId={queryId}
        onOpenChange={(open) => {
          setIsRaiseSubQueryModalOpen(open);
          if (!open) refetch();
        }}
      />
      <QueryDetailModal
        open={isSubQueryModalOpen}
        queryId={selectedSubQueryId}
        onOpenChange={(open) => {
          setIsSubQueryModalOpen(open);
          if (!open) refetch();
        }}
        onSubQueryUpdate={handleSubQueryUpdate}
        hideViewSourceButton={true}
      />
    </DialogLayout>
  );
};

export default QueryUploadModal;
